<template>
  <div class="w-full bg-truegray-100">
    <!-- HERO -->
    <div
      class="post-intro flex items-end z-40 relative px-8 py-16 lg:p-16 pt-0 text-white bg-cover bg-bottom"
      :style="[
        { backgroundImage: 'url(/img/library-bg.png)' },
        { minHeight: '500px' },
        { backgroundPosition: 'bottom center' },
      ]"
    >
      <div class="py-2 border-white lg:border-l-8 lg:pl-6">
        <h1>CareerWise Library</h1>
        <h4>Resources for Career Advisors</h4>
      </div>
    </div>

    <!-- CONTENT -->
    <div
      :class="loaded ? 'bg-truegray-100' : 'bg-truegray-500'"
      class="w-full px-8 py-16 lg:p-16"
    >
      <div
        class="transition-all duration-300 transform"
        :class="[
          loaded ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0',
        ]"
      >
        <div class="w-full border-b border-truegray-300 mb-6 pb-6">
          <h2>Pre-built Pages</h2>
          <h5>
            Get your CareerWise website ready in seconds with pre-built pages
            ready to be published
          </h5>
        </div>
        <div
          v-if="loaded"
          class="grid grid-cols-1 lg:grid-cols-3 2xl:grid-cols-4 3xl:grid-cols-5 gap-6"
        >
          <Card
            v-for="(page, i) in pages"
            :id="page.id"
            :key="i"
            :ref="'cloud-page-' + i"
            :page="page"
          />
        </div>
      </div>

      <div
        v-if="!loaded"
        class="h-52 flex flex-col justify-center items-center"
      >
        <lottie :options="loadingAnimationOptions" />
        <span class="text-white text-center">Loading Pages</span>
      </div>

      <p v-if="loaded && pages.length < 1" class="text-md">
        We could not find any pages...
      </p>

      <!-- PAGINATION -->
      <div
        v-if="pagination.previouspage || pagination.nextpage"
        class="flex justify-between items-center mt-8 h-10"
      >
        <span
          :disabled="!pagination.previouspage"
          :class="[
            !pagination.previouspage
              ? 'opacity-25 cursor-not-allowed'
              : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
          ]"
          class="text-gray-500"
          @click="getPosts(pagination.previouspage)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M11 17l-5-5m0 0l5-5m-5 5h12"
            />
          </svg>
        </span>
        <span class="text-center text-xs"
          >Page {{ pagination.current_page }} of
          {{ pagination.total_pages }}</span
        >
        <span
          :disabled="!pagination.nextpage"
          :class="[
            !pagination.nextpage
              ? 'opacity-25 cursor-not-allowed'
              : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
          ]"
          class="text-gray-500"
          @click="getPosts(pagination.nextpage)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M13 7l5 5m0 0l-5 5m5-5H6"
            />
          </svg>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { EventBus } from '../../../../../js/plugins/event-bus.js'
import { shared } from '../../../../../js/mixins/shared.js'
import Card from './Card.vue'
import * as loadingAnimation from '../../../../../shared/components/loading.json'

export default {
  components: {
    Card,
  },

  mixins: [shared],

  data() {
    return {
      component_id: false,

      loaded: false,
      loadingAnimationOptions: { animationData: loadingAnimation.default },
      loadingAnimate: false,
      pages: [],

      inputClass:
        'flex justify-center items-center sm:text-sm text-black focus:text-black active:text-black focus:outline-none active:outline-none transition-all duration-100 ease-in-out rounded bg-white px-4 h-12',

      //Not currently in use. Keep for if/when we have more pages
      pagination: {
        current_page: null,
        total_pages: null,
        per_page: 12,
        next_page: null,
        previous_page: null,
        last_page_url: null,
        next_page_url: null,
        prev_page_url: null,
        path: null,
        nextpage: null,
        previouspage: null,
      },
    }
  },

  created() {},

  mounted() {
    this.$set(this, 'component_id', this.makeId())

    this.getPosts()

    EventBus.$on('cloneCloudPage', (id) => {
      this.saveCloudPost(id)
    })
  },

  unmounted() {
    window.removeEventListener('resize', this.handleResize)
  },

  methods: {
    /**
      ============================ GET POSTS WITH FILTER OPTION ===============
    */
    getPosts(gotopage = null) {
      let submitData = {}

      // Pagination page?
      if (gotopage) {
        submitData.page = gotopage
      }

      // Get this post...
      axios
        .post('/api/auth/manager-library-pages', submitData)
        .then((response) => {
          let result = response.data
          if (result.success) {
            this.$set(this, 'pages', result.pages)
            this.$set(this, 'loaded', true)

            // pagination.total = total # of posts. / by per_page, + 1. Pagination doesnt show when not needed so +1 is never wrong on single page
            let total_pages =
              Math.floor(
                response.data.pagination.total /
                  response.data.pagination.per_page,
              ) + 1
            this.$set(
              this.pagination,
              'current_page',
              response.data.pagination.current_page,
            )
            this.$set(this.pagination, 'total_pages', total_pages)
            this.$set(
              this.pagination,
              'per_page',
              response.data.pagination.per_page,
            )
            this.$set(
              this.pagination,
              'last_page_url',
              response.data.pagination.last_page_url,
            )
            this.$set(
              this.pagination,
              'next_page_url',
              response.data.pagination.next_page_url,
            )
            this.$set(
              this.pagination,
              'prev_page_url',
              response.data.pagination.prev_page_url,
            )
            this.$set(
              this.pagination,
              'path',
              response.data.pagination.next_page_path,
            )

            let prev = null
            if (this.pagination.prev_page_url) {
              prev = this.pagination.prev_page_url.split('=')
              prev = prev[1]
            }

            let next = null
            if (this.pagination.next_page_url) {
              next = this.pagination.next_page_url.split('=')
              next = next[1]
            }

            this.$set(this.pagination, 'previouspage', prev)
            this.$set(this.pagination, 'nextpage', next)

            // Update the saved list...
            if (response.data.saved) {
              this.$store.commit(
                'careerwise/UPDATE_SAVED_CLOUDPOSTS',
                response.data.saved,
              )
            }
          } else {
            //...
          }
        })
    },
  },
}
</script>

<style scoped>
.post-intro::after {
  content: '';
  background: #fff;
  height: 1px;
  width: 500px;
  min-width: 95%;
  position: absolute;
  top: 4rem;
  right: 0;
}

.post-intro::before {
  content: '';
  background: #000;
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  right: 0;
  opacity: 0.2;
  z-index: -1;
}

.content-wrapper {
  min-height: 20rem;
}

@media screen and (max-width: 1024px) {
  .post-intro::after {
    display: none;
  }
}
</style>
